<template>
    <div class="branch-map" :class="{ 'is-zoomed': zoomedRegion }">
        <!-- ใส่ SVG แบบ inline (ไม่ใช่ <img>) เพื่อให้เปลี่ยนสีทีละจังหวัดและซูมด้วย viewBox ได้ -->
        <div v-if="svg" ref="canvas" class="branch-map-canvas" v-html="svg"></div>
        <img v-else-if="failed" class="branch-map-fallback" src="/images/map.svg" alt="แผนที่สาขา Movevai" />

        <!-- หมุดสาขาเป็น HTML ทับบน SVG: ขนาดหมุด/ตัวอักษรคงที่ไม่ว่าจะซูมแค่ไหน -->
        <div v-if="ready" class="branch-map-pins">
            <transition-group name="pin">
                <div v-for="pin of visiblePins" :key="pin.name"
                    class="bm-pin"
                    :class="['side-' + pin.side, { 'is-selected': pin.name === selected }]"
                    :style="pinStyle(pin)"
                    role="button"
                    tabindex="0"
                    :aria-label="'สาขา' + pin.name"
                    @click.stop="$emit('select', pin.name)"
                    @keydown.enter.space.prevent="$emit('select', pin.name)">
                    <span v-if="pin.name === selected" class="bm-pin-pulse"></span>
                    <span class="bm-pin-dot"></span>
                    <span class="bm-pin-label">{{ pin.name }}</span>
                </div>
            </transition-group>
        </div>

        <transition name="pin">
            <v-btn v-if="zoomedRegion" class="bm-reset" small rounded color="rgba(30,30,30,0.85)" dark
                @click="$emit('select', null)">
                <v-icon left small>mdi-arrow-expand-all</v-icon> ดูทั้งประเทศ
            </v-btn>
        </transition>
    </div>
</template>

<script>
import { BRANCH_REGIONS } from '../utils/branch-regions';

const ZOOM_DURATION = 650;

const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export default {
    props: {
        // [{ name, region, lat?, lng? }]
        branches: { type: Array, default: () => [] },
        // ชื่อสาขาที่เลือก
        selected: { type: String, default: null }
    },
    data: () => ({
        svg: '',
        failed: false,
        ready: false,
        full: null,   // viewBox ตั้งต้น (ทั้งประเทศ)
        view: null,   // viewBox ปัจจุบัน (เปลี่ยนทุกเฟรมระหว่างซูม)
        geometry: {}  // region key -> { box, marker } ในหน่วยของ viewBox
    }),
    computed: {
        selectedBranch(){
            return this.branches.find(b => b.name === this.selected) || null;
        },
        zoomedRegion(){
            return this.selectedBranch ? this.selectedBranch.region : null;
        },
        pins(){
            if (!this.ready) return [];
            return this.branches
                .filter(b => b.region && this.geometry[b.region])
                .map(b => ({ name: b.name, region: b.region, ...this.positionOf(b) }));
        },
        // แสดงหมุดเฉพาะสาขาในจังหวัดที่ซูมอยู่ หมุดซ้ายสุดวางป้ายชื่อไว้ทางซ้าย ที่เหลือวางทางขวา
        visiblePins(){
            const pins = this.spreadPins(this.pins.filter(p => p.region === this.zoomedRegion), this.zoomedRegion);
            const sorted = [...pins].sort((a, b) => a.x - b.x);
            return pins.map(p => ({ ...p, side: pins.length > 1 && p === sorted[0] ? 'left' : 'right' }));
        }
    },
    async mounted(){
        try{
            const response = await fetch('/images/map.svg');
            if (!response.ok) throw new Error(response.statusText);
            this.svg = await response.text();
            await this.$nextTick();
            this.setup();
        }catch(error){
            this.failed = true;
        }
    },
    beforeDestroy(){
        cancelAnimationFrame(this.frame);
    },
    watch: {
        selected(){
            this.applySelection();
        },
        zoomedRegion(region){
            this.zoomTo(region);
        }
    },
    methods: {
        setup(){
            const svg = this.$refs.canvas && this.$refs.canvas.querySelector('svg');
            if (!svg) return;
            this.svgEl = svg;
            svg.removeAttribute('width');
            svg.removeAttribute('height');
            svg.classList.add('branch-map-svg');
            svg.setAttribute('role', 'img');
            svg.setAttribute('aria-label', 'แผนที่สาขา Movevai');

            const [x, y, w, h] = svg.getAttribute('viewBox').split(/[\s,]+/).map(Number);
            this.full = { x, y, w, h };
            this.view = { ...this.full };

            svg.querySelectorAll('line').forEach(line => line.classList.add('bm-route'));

            // แปลงกรอบของจังหวัด/หมุดเดิมเป็นหน่วยของ viewBox (ผ่าน transform ที่ซ้อนกันหลายชั้น)
            const rootInverse = svg.getScreenCTM().inverse();
            const toRoot = el => {
                const b = el.getBBox();
                const m = rootInverse.multiply(el.getScreenCTM());
                const p1 = new DOMPoint(b.x, b.y).matrixTransform(m);
                const p2 = new DOMPoint(b.x + b.width, b.y + b.height).matrixTransform(m);
                return { x: p1.x, y: p1.y, w: p2.x - p1.x, h: p2.y - p1.y };
            };

            const geometry = {};
            this.parts = BRANCH_REGIONS.map(region => {
                const province = svg.querySelector('#' + region.province);
                const marker = svg.querySelector('#' + region.marker);
                const label = svg.querySelector('#' + region.text);
                if (province) geometry[region.key] = { box: toRoot(province), marker: marker ? toRoot(marker) : null };

                [province, marker, label].filter(Boolean).forEach(el => {
                    el.classList.add(el === province ? 'bm-province' : el === marker ? 'bm-marker' : 'bm-label');
                    el.addEventListener('click', () => this.selectRegion(region.key));
                });
                return { key: region.key, province, elements: [province, marker, label].filter(Boolean) };
            });
            this.geometry = geometry;
            this.ready = true;
            this.applySelection();
            if (this.zoomedRegion) this.zoomTo(this.zoomedRegion, false);
        },
        // คลิกจังหวัดบนแผนที่ = เลือกสาขาแรกของจังหวัดนั้น
        selectRegion(key){
            const branch = this.branches.find(b => b.region === key);
            if (branch) this.$emit('select', branch.name);
        },
        positionOf(branch){
            const region = BRANCH_REGIONS.find(r => r.key === branch.region);
            const { box, marker } = this.geometry[branch.region];
            if (region && region.bounds && typeof branch.lat === 'number' && typeof branch.lng === 'number') {
                const { minLat, maxLat, minLng, maxLng } = region.bounds;
                return {
                    x: box.x + (branch.lng - minLng) / (maxLng - minLng) * box.w,
                    y: box.y + (maxLat - branch.lat) / (maxLat - minLat) * box.h
                };
            }
            // ไม่มีพิกัด: ใช้ตำแหน่งหมุดเดิมของจังหวัด
            const anchor = marker || box;
            return { x: anchor.x + anchor.w / 2, y: anchor.y + anchor.h / 2 };
        },
        zoomWidthOf(regionKey){
            const region = BRANCH_REGIONS.find(r => r.key === regionKey);
            const { box } = this.geometry[regionKey];
            const ratio = this.full.h / this.full.w;
            return (region && region.zoomWidth) || Math.max(box.w * 1.8, box.h * 1.8 / ratio);
        },
        // สาขาที่อยู่ใกล้กันมาก (เช่น สีลม/วังบูรพา) ดันหมุดออกจากกันให้ห่างอย่างน้อย ~6% ของความกว้างแผนที่
        // ดันออกเท่ากันทั้งสองฝั่ง จุดกึ่งกลางจึงไม่เปลี่ยน
        spreadPins(pins, regionKey){
            if (pins.length < 2 || !regionKey) return pins;
            const minDist = this.zoomWidthOf(regionKey) * 0.065;
            const moved = pins.map(p => ({ ...p }));
            for (let round = 0; round < 10; round++) {
                for (let i = 0; i < moved.length; i++) {
                    for (let j = i + 1; j < moved.length; j++) {
                        const a = moved[i];
                        const b = moved[j];
                        const dx = b.x - a.x;
                        const dy = b.y - a.y;
                        const dist = Math.hypot(dx, dy) || 0.0001;
                        if (dist >= minDist) continue;
                        const push = (minDist - dist) / 2;
                        a.x -= dx / dist * push; a.y -= dy / dist * push;
                        b.x += dx / dist * push; b.y += dy / dist * push;
                    }
                }
            }
            return moved;
        },
        targetView(regionKey){
            if (!regionKey || !this.geometry[regionKey]) return { ...this.full };
            const { box } = this.geometry[regionKey];
            const pins = this.pins.filter(p => p.region === regionKey);
            const ratio = this.full.h / this.full.w; // คงสัดส่วนเดิม ไม่ให้ความสูงของแผนที่กระโดด
            const w = this.zoomWidthOf(regionKey);
            const h = w * ratio;
            // กึ่งกลางที่หมุดสาขา (ถ้ามี) ไม่ใช่กึ่งกลางจังหวัด
            const cx = pins.length ? pins.reduce((s, p) => s + p.x, 0) / pins.length : box.x + box.w / 2;
            const cy = pins.length ? pins.reduce((s, p) => s + p.y, 0) / pins.length : box.y + box.h / 2;
            const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
            return {
                x: clamp(cx - w / 2, this.full.x, this.full.x + this.full.w - w),
                y: clamp(cy - h / 2, this.full.y, this.full.y + this.full.h - h),
                w,
                h
            };
        },
        zoomTo(regionKey, animate = true){
            if (!this.ready) return;
            cancelAnimationFrame(this.frame);
            const from = { ...this.view };
            const to = this.targetView(regionKey);
            const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!animate || reduceMotion) {
                this.setView(to);
                return;
            }
            // ซูมแบบ log-scale ให้ความเร็วในการซูมดูสม่ำเสมอ ไม่พุ่งตอนท้าย
            const start = performance.now();
            const step = now => {
                const t = easeInOutCubic(Math.min((now - start) / ZOOM_DURATION, 1));
                const w = Math.exp(Math.log(from.w) + (Math.log(to.w) - Math.log(from.w)) * t);
                const k = from.w === to.w ? t : (w - from.w) / (to.w - from.w);
                this.setView({
                    x: from.x + (to.x - from.x) * k,
                    y: from.y + (to.y - from.y) * k,
                    w,
                    h: w * (this.full.h / this.full.w)
                });
                if (t < 1) this.frame = requestAnimationFrame(step);
            };
            this.frame = requestAnimationFrame(step);
        },
        setView(view){
            this.view = view;
            this.svgEl.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);
        },
        pinStyle(pin){
            const v = this.view;
            return {
                left: ((pin.x - v.x) / v.w * 100) + '%',
                top: ((pin.y - v.y) / v.h * 100) + '%'
            };
        },
        applySelection(){
            if (!this.svgEl || !this.parts) return;
            this.svgEl.classList.toggle('has-selection', !!this.zoomedRegion);
            this.parts.forEach(part => {
                const isSelected = part.key === this.zoomedRegion;
                part.elements.forEach(el => el.classList.toggle('is-selected', isSelected));
                // ย้ายจังหวัดที่เลือกไปวาดทีหลังสุด ไม่ให้จังหวัดข้างเคียงทับขอบขาว
                if (isSelected && part.province) {
                    const group = part.province.parentNode;
                    group.parentNode.appendChild(group);
                }
            });
        }
    }
};
</script>

<style>
/* ไม่ใช้ scoped เพราะ SVG มาจาก v-html จึงไม่มี data attribute ของ scoped style */
.branch-map {
    position: relative;
}
.branch-map-svg,
.branch-map-fallback {
    display: block;
    width: 100%;
    height: auto;
}
.branch-map-svg {
    overflow: hidden;
}
.branch-map-svg .bm-province,
.branch-map-svg .bm-marker,
.branch-map-svg .bm-label {
    cursor: pointer;
    transition: fill 0.35s ease, opacity 0.35s ease;
}
.branch-map-svg .bm-province:hover {
    fill: #e6b553;
}
/* ขอบขาวแบบ non-scaling: ความหนาคงที่ไม่ว่าจะซูมลึกแค่ไหน (blur filter จะเบลอเป็นก้อนตอนซูม) */
.branch-map-svg .bm-province.is-selected {
    fill: #ffcf66;
    stroke: #fff;
    stroke-width: 1.5px;
    vector-effect: non-scaling-stroke;
}
.branch-map-svg.has-selection .bm-province:not(.is-selected) {
    opacity: 0.45;
}
/* ตอนซูม ป้าย/หมุด/เส้นเดิมใน SVG จะขยายใหญ่ตาม จึงซ่อนแล้วใช้หมุด HTML แทน */
.branch-map-svg .bm-route {
    transition: opacity 0.35s ease;
}
.branch-map-svg.has-selection .bm-marker,
.branch-map-svg.has-selection .bm-label,
.branch-map-svg.has-selection .bm-route {
    opacity: 0;
    pointer-events: none;
}

.branch-map-pins {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
}
.bm-pin {
    position: absolute;
    width: 0;
    height: 0;
    pointer-events: auto;
    cursor: pointer;
    outline: none;
}
.bm-pin-dot {
    position: absolute;
    left: -6px;
    top: -6px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #1e1e1e;
    border: 2.5px solid #ffcf66;
    transition: transform 0.25s ease, background-color 0.25s ease;
}
.bm-pin.is-selected .bm-pin-dot {
    background: #ffcf66;
    border-color: #fff;
    transform: scale(1.25);
}
.bm-pin:hover .bm-pin-dot,
.bm-pin:focus-visible .bm-pin-dot {
    transform: scale(1.35);
}
.bm-pin-pulse,
.bm-pin-pulse::after {
    position: absolute;
    left: -6px;
    top: -6px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #ffcf66;
    animation: bm-pulse 1.6s ease-out infinite;
}
.bm-pin-pulse::after {
    content: '';
    left: -2px;
    top: -2px;
    animation-delay: 0.8s;
}
@keyframes bm-pulse {
    from { transform: scale(1); opacity: 0.9; }
    to { transform: scale(4); opacity: 0; }
}
.bm-pin-label {
    position: absolute;
    top: -11px;
    padding: 1px 8px;
    border-radius: 4px;
    white-space: nowrap;
    font-size: 0.8rem;
    line-height: 20px;
    color: #e0e0e0;
    background: rgba(30, 30, 30, 0.85);
    border: 1px solid rgba(196, 156, 79, 0.5);
    transition: color 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}
.bm-pin.side-right .bm-pin-label {
    left: 12px;
}
.bm-pin.side-left .bm-pin-label {
    right: 12px;
}
.bm-pin.is-selected .bm-pin-label {
    color: #1e1e1e;
    background: #ffcf66;
    border-color: #ffcf66;
    font-weight: 700;
}
.bm-pin.is-selected {
    z-index: 1;
}

.bm-reset {
    position: absolute !important;
    top: 8px;
    right: 8px;
}

.pin-enter-active {
    transition: opacity 0.3s ease 0.35s, transform 0.3s ease 0.35s;
}
.pin-leave-active {
    transition: opacity 0.15s ease;
}
.pin-enter,
.pin-leave-to {
    opacity: 0;
}
.pin-enter.bm-pin {
    transform: translateY(-8px);
}
</style>
